import { useEventCallback, useLocalStorage } from "usehooks-ts";

import type { Maintenance } from "@/hooks/useIncidentSummary";

/**
 * Local-storage key for the maintenance banners the reader has closed. Shared with apps/web, which
 * keys each dismissal the same way.
 */
export const DISMISSED_MAINTENANCES_KEY = "layer.dismissedMaintenances";

/** Old dismissals are dropped past this many, so the list never grows without bound. */
const MAX_REMEMBERED = 20;

/**
 * A dismissal covers one phase of one maintenance: closing the advance notice leaves the banner
 * to come back once the maintenance is under way.
 */
const dismissalKey = ({
  id,
  isUnderWay,
}: Pick<Maintenance, "id" | "isUnderWay">) =>
  `${id}:${isUnderWay ? "under-way" : "scheduled"}`;

/** Maintenance banners the reader has closed, remembered on this device. */
export const useDismissedMaintenances = () => {
  const [dismissed, setDismissed] = useLocalStorage<string[]>(
    DISMISSED_MAINTENANCES_KEY,
    [],
  );

  const isDismissed = (maintenance: Pick<Maintenance, "id" | "isUnderWay">) =>
    Array.isArray(dismissed) && dismissed.includes(dismissalKey(maintenance));

  const dismiss = useEventCallback(
    (maintenance: Pick<Maintenance, "id" | "isUnderWay">) => {
      const key = dismissalKey(maintenance);
      setDismissed((previous) =>
        [
          ...(Array.isArray(previous) ? previous : []).filter(
            (existing) => existing !== key,
          ),
          key,
        ].slice(-MAX_REMEMBERED),
      );
    },
  );

  return { isDismissed, dismiss };
};
